import { supabase } from '@/lib/supabase';
import { NUTRIENT_CONFIG, type NutrientKey } from '@/hooks/useMicronutrients';
import { buildRainbow, type WeekRation } from '@/lib/weekRation';

// ════════════════════════════════════════════════════════════════
// Круги разбора недели (11.10.2026, идея Славы).
//
// «Дофи ждёт серию, делает анализ и висит. Посмотрел анализ — пусть предлагает:
// пройди ещё одну серию, и анализ расширится. Надо придумать, что там будет
// такого, чего не даст чат с Дофи».
//
// Чего чат не даёт: он видит сегодняшний день и короткую сводку. Здесь — расчёт
// по двум неделям записей, с графиками и сравнением человека с самим собой.
// Модели нет вовсе: всё складывается из сохранённых приёмов, 0 ₽ и мгновенно.
//
//   Круг 1 — витамины, минералы и радуга (weekRation.ts)
//   Круг 2 — ритм питания: когда ешь, сколько уходит на вечер, где нет белка
//   Круг 3 — твои продукты: что закрывает норму и чем из своего закрыть провалы
//   Круг 4 — динамика: было → стало против прошлой недели
// Дальше каждый круг показывает всё и добавляет новую строку в динамику.
// ════════════════════════════════════════════════════════════════

export const LEVELS = [
  { n: 1, emoji: '🧪', short: 'Витамины', title: 'Витамины и минералы', promise: 'чего в рационе хватает, чего нет, и радуга недели' },
  { n: 2, emoji: '⏰', short: 'Ритм', title: 'Ритм питания', promise: 'в какие часы ты ешь, сколько уходит на вечер и в каком приёме не хватает белка' },
  { n: 3, emoji: '🏆', short: 'Продукты', title: 'Твои продукты', promise: 'какие продукты закрывают твою норму и чем из своего же закрыть провалы' },
  { n: 4, emoji: '📈', short: 'Динамика', title: 'Динамика', promise: 'что изменилось против прошлой недели: было → стало' },
] as const;

export const MAX_LEVEL = LEVELS.length;

const COLUMN: Record<string, string> = {
  fiber: 'fiber_g', vegetables_fruits: 'vegetables_fruits_g', omega3: 'omega3_g',
  calcium: 'calcium_mg', iron: 'iron_mg', magnesium: 'magnesium_mg', zinc: 'zinc_mg', potassium: 'potassium_mg',
  vitamin_d: 'vitamin_d_mcg', vitamin_b12: 'vitamin_b12_mcg', vitamin_c: 'vitamin_c_mg',
};

type Item = Record<string, any> & { name: string };
type MealRow = { consumed_at: string; meal_type: string; total_calories: number; total_protein_g: number; items: Item[] };

export type Rhythm = {
  days: number;
  firstAt: string; // «08:40»
  lastAt: string;
  windowHours: number;
  /** доли калорий по частям дня, в процентах, сумма 100 */
  parts: { id: 'morning' | 'day' | 'evening' | 'late'; label: string; percent: number }[];
  /** белок в среднем за день по приёмам, граммы */
  protein: { type: string; label: string; grams: number }[];
  notes: string[];
};

export type Sources = {
  strong: { key: string; label: string; emoji: string; top: { name: string; share: number }[] }[];
  weak: { key: string; label: string; emoji: string; top: string[] }[];
  unique: number;
  frequent: { name: string; times: number }[];
};

export type Dynamics = {
  /** прошлая неделя записана достаточно, чтобы сравнивать */
  comparable: boolean;
  prevDays: number;
  up: { label: string; emoji: string; from: number; to: number }[];
  down: { label: string; emoji: string; from: number; to: number }[];
  calories: { from: number; to: number } | null;
  protein: { from: number; to: number } | null;
  rainbow: { from: number; to: number } | null;
  late: { from: number; to: number } | null;
};

export type WeekExtras = { rhythm: Rhythm | null; sources: Sources | null; dynamics: Dynamics | null };

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(Math.round(min % 60)).padStart(2, '0')}`;
const avg = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0);
const norm = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');
const cap = (name: string) => name.charAt(0).toUpperCase() + name.slice(1);

function byDay(meals: MealRow[]): Map<string, MealRow[]> {
  const m = new Map<string, MealRow[]>();
  for (const r of meals) {
    const k = ymd(new Date(r.consumed_at));
    m.set(k, [...(m.get(k) ?? []), r]);
  }
  return m;
}

/** Доля калорий после 21:00, в процентах */
function lateShare(meals: MealRow[]): number {
  const total = meals.reduce((s, m) => s + (Number(m.total_calories) || 0), 0);
  if (!total) return 0;
  const late = meals
    .filter((m) => new Date(m.consumed_at).getHours() >= 21)
    .reduce((s, m) => s + (Number(m.total_calories) || 0), 0);
  return Math.round((late / total) * 100);
}

function buildRhythm(meals: MealRow[]): Rhythm | null {
  const days = byDay(meals);
  if (days.size < 3) return null;
  const firsts: number[] = [];
  const lasts: number[] = [];
  for (const rows of days.values()) {
    const mins = rows.map((r) => {
      const d = new Date(r.consumed_at);
      return d.getHours() * 60 + d.getMinutes();
    });
    firsts.push(Math.min(...mins));
    lasts.push(Math.max(...mins));
  }
  const first = avg(firsts);
  const last = avg(lasts);

  const bucket = { morning: 0, day: 0, evening: 0, late: 0 };
  for (const m of meals) {
    const h = new Date(m.consumed_at).getHours();
    const k = h < 12 ? 'morning' : h < 17 ? 'day' : h < 21 ? 'evening' : 'late';
    bucket[k] += Number(m.total_calories) || 0;
  }
  const total = bucket.morning + bucket.day + bucket.evening + bucket.late || 1;
  const pct = (v: number) => Math.round((v / total) * 100);
  const parts: Rhythm['parts'] = [
    { id: 'morning', label: 'до 12:00', percent: pct(bucket.morning) },
    { id: 'day', label: '12–17', percent: pct(bucket.day) },
    { id: 'evening', label: '17–21', percent: pct(bucket.evening) },
    { id: 'late', label: 'после 21', percent: 0 },
  ];
  parts[3].percent = Math.max(0, 100 - parts[0].percent - parts[1].percent - parts[2].percent);

  const TYPES: [string, string][] = [['breakfast', 'Завтрак'], ['lunch', 'Обед'], ['dinner', 'Ужин'], ['snack', 'Перекусы']];
  const protein = TYPES.map(([type, label]) => ({
    type,
    label,
    grams: Math.round(
      meals.filter((m) => m.meal_type === type).reduce((s, m) => s + (Number(m.total_protein_g) || 0), 0) / days.size,
    ),
  }));

  const notes: string[] = [];
  const windowHours = Math.round(((last - first) / 60) * 10) / 10;
  if (parts[3].percent >= 20) {
    notes.push(`${parts[3].percent}% калорий приходится на время после 21:00. Это не запрет — просто знай, что сон после плотного позднего ужина обычно хуже.`);
  } else if (parts[2].percent + parts[3].percent >= 55) {
    notes.push('Больше половины дневной еды у тебя вечером. Если днём бывает упадок сил — попробуй сдвинуть часть на обед.');
  } else if (parts[0].percent + parts[1].percent >= 60) {
    notes.push('Основная еда приходится на первую половину дня — такой ритм обычно легче для сна и вечернего аппетита.');
  }
  const main = protein.filter((p) => p.type !== 'snack' && p.grams > 0);
  if (main.length >= 2) {
    const low = main.reduce((a, b) => (b.grams < a.grams ? b : a));
    const high = main.reduce((a, b) => (b.grams > a.grams ? b : a));
    if (high.grams >= low.grams * 2 && low.grams < 20) {
      notes.push(`Меньше всего белка — ${low.label.toLowerCase()}: в среднем ${low.grams} г. Яйцо, творог или рыба в этот приём выровняют день.`);
    }
  }
  if (windowHours >= 14) {
    notes.push(`Между первым и последним приёмом в среднем ${String(windowHours).replace('.', ',')} ч — пищеварение почти не отдыхает.`);
  }

  return { days: days.size, firstAt: hhmm(first), lastAt: hhmm(last), windowHours, parts, protein, notes };
}

function sumBy(rows: Item[], col: string): [string, number][] {
  const m = new Map<string, number>();
  for (const i of rows) {
    const v = Number(i[col]) || 0;
    if (v > 0) m.set(norm(i.name), (m.get(norm(i.name)) ?? 0) + v);
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

function buildSources(week: MealRow[], both: MealRow[], data: WeekRation): Sources | null {
  const items = week.flatMap((m) => m.items ?? []);
  if (!items.length) return null;

  const strong = data.strong
    .filter((r) => COLUMN[r.key])
    .map((r) => {
      const ranked = sumBy(items, COLUMN[r.key]);
      const total = ranked.reduce((s, [, v]) => s + v, 0) || 1;
      return {
        key: r.key as string,
        label: r.label,
        emoji: r.emoji,
        top: ranked.slice(0, 2).map(([name, v]) => ({ name: cap(name), share: Math.round((v / total) * 100) })),
      };
    })
    .filter((r) => r.top.length);

  // Провалы закрываем СВОИМИ же продуктами за две недели: человек их уже ест и покупает,
  // совет «добавь ещё раз» выполним. Незнакомых продуктов не предлагаем.
  const all = both.flatMap((m) => m.items ?? []);
  const weak = data.weak
    .filter((r) => COLUMN[r.key])
    .map((r) => ({
      key: r.key as string,
      label: r.label,
      emoji: r.emoji,
      top: sumBy(all, COLUMN[r.key]).slice(0, 3).map(([name]) => cap(name)),
    }))
    .filter((r) => r.top.length);

  const count = new Map<string, number>();
  for (const i of items) count.set(norm(i.name), (count.get(norm(i.name)) ?? 0) + 1);
  const frequent = [...count.entries()]
    .sort((a, b) => b[1] - a[1])
    .filter(([, n]) => n >= 2)
    .slice(0, 5)
    .map(([name, times]) => ({ name: cap(name), times }));

  return { strong, weak, unique: count.size, frequent };
}

function percentByNutrient(meals: MealRow[], targets: Record<string, number>): Record<string, number> {
  const days = byDay(meals).size || 1;
  const items = meals.flatMap((m) => m.items ?? []);
  const out: Record<string, number> = {};
  for (const [key, col] of Object.entries(COLUMN)) {
    const target = targets[key];
    if (!(target > 0)) continue;
    const total = items.reduce((s, i) => s + (Number(i[col]) || 0), 0);
    out[key] = Math.round((total / days / target) * 100);
  }
  return out;
}

function buildDynamics(week: MealRow[], prev: MealRow[], data: WeekRation): Dynamics {
  const prevDays = byDay(prev).size;
  const nowDays = byDay(week).size || 1;
  // Сравниваем, только если прошлая неделя записана хотя бы тремя днями:
  // иначе «рост на 40%» означал бы «на прошлой неделе не записывал».
  if (prevDays < 3) {
    return { comparable: false, prevDays, up: [], down: [], calories: null, protein: null, rainbow: null, late: null };
  }

  const now = percentByNutrient(week, data.targets);
  const was = percentByNutrient(prev, data.targets);
  const rows = Object.keys(now)
    .filter((k) => was[k] != null && (now[k] > 0 || was[k] > 0))
    .map((k) => {
      const cfg = NUTRIENT_CONFIG[k as NutrientKey];
      return { label: cfg?.label ?? k, emoji: cfg?.emoji ?? '', from: was[k], to: now[k], delta: now[k] - was[k] };
    });
  const strip = (r: (typeof rows)[number]) => ({ label: r.label, emoji: r.emoji, from: r.from, to: r.to });
  const perDay = (ms: MealRow[], f: (m: MealRow) => number, d: number) => Math.round(ms.reduce((s, m) => s + f(m), 0) / d);
  const names = (ms: MealRow[]) => ms.flatMap((m) => (m.items ?? []).map((i) => norm(i.name)));

  return {
    comparable: true,
    prevDays,
    up: rows.filter((r) => r.delta >= 10).sort((a, b) => b.delta - a.delta).slice(0, 3).map(strip),
    down: rows.filter((r) => r.delta <= -10).sort((a, b) => a.delta - b.delta).slice(0, 2).map(strip),
    calories: { from: perDay(prev, (m) => Number(m.total_calories) || 0, prevDays), to: perDay(week, (m) => Number(m.total_calories) || 0, nowDays) },
    protein: { from: perDay(prev, (m) => Number(m.total_protein_g) || 0, prevDays), to: perDay(week, (m) => Number(m.total_protein_g) || 0, nowDays) },
    rainbow: { from: buildRainbow(names(prev)).found.length, to: buildRainbow(names(week)).found.length },
    late: { from: lateShare(prev), to: lateShare(week) },
  };
}

/**
 * Данные для кругов 2–4. Один запрос за две недели приёмов с продуктами —
 * круг определяет только, что из посчитанного показать.
 */
export async function getWeekExtras(data: WeekRation): Promise<WeekExtras> {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const weekStart = new Date();
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - 6);
  const prevStart = new Date(weekStart);
  prevStart.setDate(prevStart.getDate() - 7);

  const { data: rows, error } = await supabase
    .from('meals')
    .select(`consumed_at, meal_type, total_calories, total_protein_g, items:meal_items(name, ${Object.values(COLUMN).join(', ')})`)
    .gte('consumed_at', prevStart.toISOString())
    .lte('consumed_at', end.toISOString())
    .order('consumed_at', { ascending: true })
    .limit(2000);
  if (error) {
    console.error('[weekLevels] load failed', error);
    return { rhythm: null, sources: null, dynamics: null };
  }
  const all = (rows ?? []) as unknown as MealRow[];
  const week = all.filter((m) => new Date(m.consumed_at) >= weekStart);
  const prev = all.filter((m) => new Date(m.consumed_at) < weekStart);
  return {
    rhythm: buildRhythm(week),
    sources: buildSources(week, all, data),
    dynamics: buildDynamics(week, prev, data),
  };
}
